import { ReactNode } from "react";
import { accountSettings, getDigestStatus } from "@pjs/user-domain";
import { catchError, easeLoading, EMPTY, map, of, share, switchMap } from "@pjs/observables";
import { AsyncModel, createAsyncModelError, createAsyncModelPending, createAsyncModelResult } from "@pjs/utilities";
import { actionLogger } from "@pjs/a11y";
import { notificationsIcon, LoadingSpinner } from "@pjs/core-ui";
import { IViewConfig } from "../interfaces/IViewConfig";
import { userUiI18n } from "../../i18n/UserUiI18n.const";
import { getNotificationValues } from "../../notifications/utils/GetNotificationValues.function";
import { Notifications } from "../../notifications/Notifications.component";
import { accountSettingsActions } from "../../actions/AccountSettingsActions.const";
import { View } from "../enums/View";
import { AccountSettingsError } from "../AccountSettingsError.component";

export const notificationsView: IViewConfig = {
    id: View.Notifications,
    page: {
        get title() {
            return userUiI18n.getString("notifications.title");
        },
        get description() {
            return userUiI18n.getString("notifications.description");
        }
    },
    navigation: {
        icon: notificationsIcon,
        get label() {
            return userUiI18n.getString("settings_sidebar.buttons.notifications");
        }
    },
    component: () => {
        const getData = getDigestStatus().pipe(
            switchMap((status) => {
                return accountSettings.getModel().pipe(
                    map((settings) => {
                        const data = getNotificationValues({
                            ...settings.notifications,
                            digestIsDisabled: status
                        });

                        return createAsyncModelResult(<Notifications model={data} actions={accountSettingsActions.notifications} />);
                    })
                );
            }),
            catchError(() => {
                const title = userUiI18n.getString("settings_view.loading_error.title");
                const content = userUiI18n.getString("settings_view.loading_error.content");
                const loggerMessage = userUiI18n.getString("notifications.logger.page_load_error");
                actionLogger.append(loggerMessage);

                return of(createAsyncModelError(<AccountSettingsError title={title} content={content} />));
            }),
            share()
        );

        return easeLoading<AsyncModel<ReactNode, ReactNode, ReactNode>>(
            createAsyncModelPending(<LoadingSpinner className="uui-settings-view__loading-spinner" />),
            getData
        );
    },
    isEnabled: (_userDetails, settingsModel) => settingsModel.notifications.enabled,
    state: () => {
        return EMPTY;
    },
    clear: () => {
        accountSettings.clear();
    }
};
